import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, Button, Input, Select, Empty, Badge } from "../../components/admin/UI";
import { Modal, useModal } from "../../components/admin/Modal";
import { Copy, KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { downloadXlsx } from "../../lib/download";

export default function Parents() {
    const { activeClubId, activeSeasonId, roleInActiveClub, isSuperAdmin } = useAuth();
    const [parents, setParents] = useState([]);
    const [players, setPlayers] = useState([]);
    const [filterPlayer, setFilterPlayer] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const { open, openModal, closeModal } = useModal();
    const empty = { first_name: "", last_name: "", relation: "pere", phone: "", email: "", address: "", occupation: "", player_id: "" };
    const [form, setForm] = useState(empty);
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState(empty);
    const [editSaving, setEditSaving] = useState(false);
    const [emailTarget, setEmailTarget] = useState(null);
    const [accountEmail, setAccountEmail] = useState("");
    const [emailConfirmed, setEmailConfirmed] = useState(false);
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [activationResult, setActivationResult] = useState(null);
    const [exporting, setExporting] = useState(false);
    const canExport = isSuperAdmin || ["club_admin", "staff"].includes(roleInActiveClub);
    const canEdit = isSuperAdmin || roleInActiveClub === "club_admin";

    const exportExcel = async () => {
        setExporting(true);
        try {
            const params = { club_id: activeClubId, season_id: activeSeasonId };
            if (filterPlayer) params.player_id = filterPlayer;
            if (debouncedSearch) params.search = debouncedSearch;
            await downloadXlsx("/exports/parents", params, "parents.xlsx");
        } catch (error) {
            toast.error(error.message);
        } finally {
            setExporting(false);
        }
    };

    const load = () => {
        if (!activeClubId) return;
        setLoading(true);
        setLoadError(false);
        const params = { club_id: activeClubId };
        if (activeSeasonId) params.season_id = activeSeasonId;
        const q2 = { club_id: activeClubId };
        if (activeSeasonId) q2.season_id = activeSeasonId;
        if (filterPlayer) q2.player_id = filterPlayer;
        if (debouncedSearch) q2.search = debouncedSearch;
        Promise.all([api.get("/players", { params }), api.get("/parents", { params: q2 })])
            .then(([playerResponse, parentResponse]) => {
                setPlayers(playerResponse.data);
                setParents(parentResponse.data);
            })
            .catch((error) => {
                setLoadError(true);
                toast.error(formatApiError(error));
            })
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [search]);
    useEffect(load, [activeClubId, activeSeasonId, filterPlayer, debouncedSearch]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/parents", form, { params: { club_id: activeClubId } });
            toast.success("Parent ajouté");
            closeModal();
            setForm(empty);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer ce parent ?")) return;
        await api.delete(`/parents/${id}`, { params: { club_id: activeClubId } });
        toast.success("Parent supprimé");
        load();
    };

    const openEdit = (parent) => {
        setEditTarget(parent);
        setEditForm({
            first_name: parent.first_name || "",
            last_name: parent.last_name || "",
            relation: parent.relation || "tuteur",
            phone: parent.phone || "",
            email: parent.email || "",
            address: parent.address || "",
            occupation: parent.occupation || "",
        });
    };

    const submitEdit = async (event) => {
        event.preventDefault();
        if (editSaving) return;
        setEditSaving(true);
        try {
            const payload = { ...editForm };
            delete payload.email;
            for (const field of ["phone", "email", "address", "occupation"]) {
                if (!(field in payload)) continue;
                if (!payload[field]) payload[field] = null;
            }
            await api.patch(`/parents/${editTarget.id}`, payload, { params: { club_id: activeClubId } });
            toast.success("Informations du responsable mises à jour");
            setEditTarget(null);
            load();
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setEditSaving(false);
        }
    };

    const openEmailChange = (parent) => {
        setEmailTarget(parent);
        setAccountEmail(parent.account_email || "");
        setEmailConfirmed(false);
        setEmailError("");
        setActivationResult(null);
    };

    const submitEmailChange = async (event) => {
        event.preventDefault();
        if (emailSaving || !emailConfirmed) return;
        setEmailSaving(true);
        setEmailError("");
        try {
            const response = await api.post(
                `/parents/${emailTarget.id}/account-email-change`,
                { email: accountEmail, confirmed: true },
                { params: { club_id: activeClubId } },
            );
            setActivationResult(response.data);
            toast.success(response.data.changed ? "E-mail de connexion modifié" : "L'e-mail de connexion est inchangé");
            load();
        } catch (error) {
            const detail = error?.response?.data?.detail;
            if (detail?.code === "ACCOUNT_EMAIL_CHANGE_REQUIRES_ACTIVATION" && detail.activation_url) {
                setActivationResult({ ...detail, awaiting_activation: true, recovery_required: true });
                toast.error(detail.message);
                load();
                return;
            }
            const message = detail?.code === "EMAIL_ALREADY_IN_USE"
                ? "Cette adresse e-mail est déjà utilisée par un autre compte."
                : formatApiError(error);
            setEmailError(message);
            toast.error(message);
        } finally {
            setEmailSaving(false);
        }
    };

    const copyActivationLink = async () => {
        if (!activationResult?.activation_url) return;
        await navigator.clipboard.writeText(activationResult.activation_url);
        toast.success("Lien d'activation copié");
    };

    const playerName = (id) => {
        const p = players.find((x) => x.id === id);
        return p ? `${p.last_name?.toUpperCase()} ${p.first_name}` : "—";
    };
    const relationLabel = (r) => ({ pere: "Père", mere: "Mère", tuteur: "Tuteur" }[r]) || r;

    return (
        <div data-testid="parents-page">
            <PageHeader
                title="Parents & Tuteurs"
                subtitle="Contacts familiaux des joueurs"
                action={<div className="flex gap-2">
                    {canExport && <Button variant="secondary" onClick={exportExcel} disabled={!activeClubId || !activeSeasonId || exporting} data-testid="export-parents-button">Exporter Excel</Button>}
                    <Button onClick={openModal} disabled={!players.length} data-testid="add-parent-button"><Plus className="w-4 h-4" /> Ajouter</Button>
                </div>}
            />

            <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex items-end gap-2 w-full sm:w-auto">
                    <Input
                        label="Rechercher un parent ou un joueur"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Nom, téléphone ou e-mail"
                        className="w-full sm:w-80"
                        data-testid="parents-search"
                    />
                    {search && <Button variant="outline" onClick={() => setSearch("")} data-testid="parents-search-clear">Effacer</Button>}
                </div>
                <Select label="Filtrer par joueur" value={filterPlayer} onChange={(e) => setFilterPlayer(e.target.value)} className="w-64" data-testid="parents-player-filter">
                    <option value="">Tous</option>
                    {players.map((p) => <option key={p.id} value={p.id}>{p.last_name?.toUpperCase()} {p.first_name}</option>)}
                </Select>
            </div>

            {loading ? (
                <div className="py-16 text-center text-sm text-slate-500" role="status">Chargement...</div>
            ) : loadError ? (
                <Empty title="Impossible de charger les parents" hint="Veuillez réessayer." />
            ) : parents.length === 0 ? (
                <Empty title="Aucun parent" hint="Liez des parents ou tuteurs à vos joueurs." />
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr className="text-left">
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Nom</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Relation</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Joueur</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Téléphone</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</th>
                                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parents.map((p) => (
                                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                                        <td className="px-4 py-3 font-semibold text-slate-900">{p.last_name?.toUpperCase()} {p.first_name}</td>
                                        <td className="px-4 py-3"><Badge color="blue">{relationLabel(p.relation)}</Badge></td>
                                        <td className="px-4 py-3">{playerName(p.player_id)}</td>
                                        <td className="px-4 py-3">{p.phone || "—"}</td>
                                        <td className="px-4 py-3">{p.email || "—"}</td>
                                        <td className="px-4 py-3 text-right">
                                            {canEdit && <button data-testid={`edit-parent-${p.id}`} title="Modifier les informations du responsable" aria-label="Modifier les informations du responsable" onClick={() => openEdit(p)} className="mr-3 text-slate-500 hover:text-orange-600"><Pencil className="w-4 h-4 inline" /></button>}
                                            {canEdit && p.user_id && p.account_email_change_allowed !== false && <button data-testid={`change-parent-login-email-${p.id}`} title="Modifier l’e-mail de connexion" aria-label="Modifier l’e-mail de connexion" onClick={() => openEmailChange(p)} className="mr-3 text-slate-500 hover:text-orange-600"><KeyRound className="w-4 h-4 inline" /></button>}
                                            <button onClick={() => remove(p.id)} className="text-slate-500 hover:text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            <Modal open={open} onClose={closeModal} title="Ajouter un parent / tuteur">
                <form onSubmit={submit} className="space-y-4" data-testid="parent-form">
                    <div className="grid grid-cols-2 gap-3">
                        <Input label="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
                        <Input label="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
                        <Select label="Relation" value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })}>
                            <option value="pere">Père</option>
                            <option value="mere">Mère</option>
                            <option value="tuteur">Tuteur</option>
                        </Select>
                        <Select label="Joueur" value={form.player_id} onChange={(e) => setForm({ ...form, player_id: e.target.value })} required>
                            <option value="">Sélectionner...</option>
                            {players.map((p) => <option key={p.id} value={p.id}>{p.last_name?.toUpperCase()} {p.first_name}</option>)}
                        </Select>
                        <Input label="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        <Input label="Profession" value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} className="col-span-2" />
                        <Input label="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="col-span-2" />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" type="button" onClick={closeModal}>Annuler</Button>
                        <Button type="submit" data-testid="parent-submit">Créer</Button>
                    </div>
                </form>
            </Modal>

            <Modal open={Boolean(editTarget)} onClose={() => !editSaving && setEditTarget(null)} title="Informations du responsable">
                <form onSubmit={submitEdit} className="space-y-4" data-testid="parent-edit-form">
                    <p className="text-sm text-slate-600">Ces coordonnées concernent uniquement la relation avec ce joueur. Elles ne modifient ni le compte de connexion ni les autres enfants.</p>
                    <div className="grid grid-cols-2 gap-3">
                        <Input label="Prénom" value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} required />
                        <Input label="Nom" value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} required />
                        <Select label="Relation" value={editForm.relation} onChange={(e) => setEditForm({ ...editForm, relation: e.target.value })}>
                            <option value="pere">Père</option><option value="mere">Mère</option><option value="tuteur">Tuteur</option>
                        </Select>
                        <Input label="Téléphone du responsable" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                        <div className="col-span-2">
                            <Input label="E-mail du responsable" type="email" value={editForm.email} readOnly disabled data-testid="parent-email-readonly" />
                            <p className="mt-1 text-xs text-slate-500">L’adresse e-mail ne peut pas être modifiée depuis cette fiche.</p>
                        </div>
                        <Input label="Profession" value={editForm.occupation} onChange={(e) => setEditForm({ ...editForm, occupation: e.target.value })} className="col-span-2" />
                        <Input label="Adresse" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="col-span-2" />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" type="button" onClick={() => setEditTarget(null)} disabled={editSaving}>Annuler</Button>
                        <Button type="submit" disabled={editSaving} data-testid="parent-edit-submit">{editSaving ? "Enregistrement..." : "Enregistrer"}</Button>
                    </div>
                </form>
            </Modal>

            <Modal open={Boolean(emailTarget)} onClose={() => !emailSaving && setEmailTarget(null)} title="Modifier l’e-mail de connexion">
                {activationResult?.activation_url ? (
                    <div className="space-y-4" data-testid="parent-activation-result">
                        <p className="text-sm"><strong>Nouvel e-mail de connexion :</strong> {activationResult.email}</p>
                        {activationResult.recovery_required && <p className="text-sm text-red-700">La mise à jour des coordonnées du responsable doit être vérifiée manuellement.</p>}
                        <p className="text-sm text-amber-700">Le compte attend son activation. Envoyez ce lien à la nouvelle adresse e-mail.</p>
                        <Input label="Lien d’activation" value={activationResult.activation_url} readOnly data-testid="parent-activation-link" />
                        <div className="flex justify-end gap-2"><Button variant="secondary" onClick={copyActivationLink} data-testid="copy-parent-activation-link"><Copy className="w-4 h-4" /> Copier</Button><Button onClick={() => setEmailTarget(null)}>Fermer</Button></div>
                    </div>
                ) : (
                    <form onSubmit={submitEmailChange} className="space-y-4" data-testid="parent-account-email-form">
                        <div className="rounded-lg bg-slate-50 p-3 text-sm"><strong>E-mail actuel :</strong> {emailTarget?.account_email || "Non disponible"}</div>
                        <Input label="Nouvelle adresse e-mail de connexion" type="email" value={accountEmail} onChange={(e) => setAccountEmail(e.target.value)} required data-testid="parent-account-email-input" />
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">L’identifiant actuel cessera de fonctionner, les sessions existantes seront invalidées et le compte devra définir un nouveau mot de passe avec un nouveau lien d’activation.</div>
                        <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={emailConfirmed} onChange={(e) => setEmailConfirmed(e.target.checked)} data-testid="parent-account-email-confirm" /><span>Je confirme ce changement de compte et la nouvelle activation.</span></label>
                        {emailError && <p className="text-sm text-red-600" role="alert">{emailError}</p>}
                        <div className="flex justify-end gap-2"><Button variant="secondary" type="button" onClick={() => setEmailTarget(null)} disabled={emailSaving}>Annuler</Button><Button type="submit" disabled={!emailConfirmed || emailSaving} data-testid="parent-account-email-submit">{emailSaving ? "Préparation..." : "Modifier et générer le lien"}</Button></div>
                    </form>
                )}
            </Modal>
        </div>
    );
}
